import React, { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { Toaster } from '../ui/sonner';
import { cn } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Eye, X } from 'lucide-react';
import { Badge } from '../ui/badge';
import { DemoToolkit } from '../demo/DemoToolkit';
import { GlossaryMode } from '../common/GlossaryMode';
import { ToolkitFAB } from './ToolkitFAB';
import { CommandPalette } from '../common/CommandPalette';
import MissionControlShortcuts from './MissionControlShortcuts';
import useNavShortcuts from '../../hooks/useNavShortcuts';
import OnboardingBanner from './OnboardingBanner';
import { PageTitleProvider } from '../../context/PageTitleContext';
import { useDemoTools } from '../../hooks/useDemoTools';
import { isWarmupFrame } from '../../lib/pageCache';
import { useAutoWarmupScheduler } from '../../hooks/useDemoWarmup';

// Cross-surface `g + letter` shortcuts available everywhere in Mission Control.
// Memoised at module scope so the hook's effect doesn't re-bind every render.
const MC_NAV_SHORTCUTS = { m: '/mirror', h: '/' };

const ROLE_NAMES = { super_admin: 'Super Admin', org_admin: 'Org Admin', user: 'User' };

const ImpersonationBanner = () => {
  const { impersonating, stopImpersonation } = useAuth();
  if (!impersonating) return null;
  return (
    <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between z-50" data-testid="impersonation-banner">
      <div className="flex items-center gap-2">
        <Eye className="h-4 w-4" />
        <span className="text-sm font-semibold">
          Viewing as <strong>{impersonating.name}</strong> ({impersonating.email})
        </span>
        <Badge className="bg-white/20 text-white border-white/30 text-xs">{ROLE_NAMES[impersonating.role] || impersonating.role}</Badge>
        {impersonating.organization_name && <span className="text-xs opacity-80">| {impersonating.organization_name}</span>}
      </div>
      <button onClick={stopImpersonation} className="flex items-center gap-1 px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-sm font-medium transition-colors" data-testid="stop-impersonation-btn">
        <X className="h-3.5 w-3.5" /> Stop Impersonating
      </button>
    </div>
  );
};

export const DashboardLayout = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { user, viewAsOrg } = useAuth();
  const { resolvedTheme } = useTheme();
  const [demoTools] = useDemoTools();
  const effectiveOrgId = viewAsOrg?.id || user?.organization_id;
  useAutoWarmupScheduler(isWarmupFrame() ? null : effectiveOrgId);
  useNavShortcuts(MC_NAV_SHORTCUTS);
  const isDark = resolvedTheme === 'dark';

  return (
    <div className={cn('min-h-screen theme-surface', isDark ? 'bg-[#0a0f1c]' : 'bg-slate-50')}>
      <PageTitleProvider>
      <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />
      <div
        className={cn(
          'transition-all duration-300',
          isCollapsed ? 'ml-16' : 'ml-64'
        )}
        style={{ '--mc-content-left': isCollapsed ? '4rem' : '16rem' }}
      >
        <ImpersonationBanner />
        <Header />
        <OnboardingBanner orgId={effectiveOrgId} />
        <main className={cn('p-6 md:p-8', isDark ? 'home-dark-scope' : 'app-light')}>
          <Outlet />
        </main>
      </div>
      </PageTitleProvider>
      {!isWarmupFrame() && <DemoToolkit />}
      <GlossaryMode />
      {/* "Tools" FAB — shown only when demo tools are enabled (Profile
          settings or the Home header toggle). */}
      {demoTools && !isWarmupFrame() && <ToolkitFAB />}
      <CommandPalette />
      <MissionControlShortcuts />
      <Toaster position="top-right" richColors />
    </div>
  );
};

export default DashboardLayout;
